import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useRef, useState } from 'react';
import type {
  Stripe,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';

import { CheckoutContext } from '@/context/CheckoutContext';
import { AMAZON_PAY_PAYMENT_CONFIG } from '@/constants/stripe';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { logError } from '@/utils/logger';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { AmazonPayButtonPlaceholder } from '@/components/Wallets/WalletButton/AmazonPayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

interface AmazonPayStripeFormProps {
  /** Optional callback that surfaces ZIP collection inline instead of a global error. */
  onInvalidTaxClick?: () => void;
  isExpress?: boolean;
}

interface AmazonPayButtonUIProps {
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onLoadError?: (error?: unknown) => void;
  onInvalidTaxClick?: () => void;
}

const AmazonPayButtonUI = ({ onReady, onConfirm, onLoadError, onInvalidTaxClick }: AmazonPayButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
  } = context;
  const { t } = useTranslate();

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (!isFormValid) {
      if (onInvalidTaxClick) {
        onInvalidTaxClick();
      } else {
        setError(t(getZipCodeErrorMessageKey(customer)));
      }
      event.reject();
      return;
    }
    trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.AMAZON_PAY });
    notifyPaymentIntentClick(context, { paymentMethodName: PaymentMethodEnum.AMAZON_PAY });
    // Amazon Pay ECE authenticates the shopper via Amazon's hosted popup;
    // requesting the email here mirrors Apple/Google Pay so the receipt path
    // still works when the checkout doesn't already have a customer email.
    event.resolve({ emailRequired: true });
  };

  return (
    <ExpressCheckoutElement
      onClick={handleClick}
      onConfirm={onConfirm}
      onReady={onReady}
      onLoadError={onLoadError}
      options={AMAZON_PAY_PAYMENT_CONFIG}
    />
  );
};

interface AmazonPayButtonProps {
  /** The instance behind the enclosing `<Elements>` — the payment must run on it. */
  stripe: Stripe;
  onReady: () => void;
  onUnavailable: (reason: string) => void;
  onInvalidTaxClick?: () => void;
}

const AmazonPayButton = ({ stripe, onReady, onUnavailable, onInvalidTaxClick }: AmazonPayButtonProps) => {
  const {
    actions: { runPayment },
  } = useContext(CheckoutContext);
  const elements = useElements();

  const handleLoadError = (error?: unknown) => {
    logError('Amazon Pay load error', error);
    onUnavailable('load_error');
  };

  /**
   * Stripe ECE fires `onReady` even when Amazon Pay is unavailable on this
   * device/browser (per Stripe's support matrix — e.g. Firefox on iOS, Edge
   * on iOS) — it just leaves the button unpainted. Per Stripe's type:
   *   availablePaymentMethods: undefined | AvailablePaymentMethods
   *   "...undefined if no payment methods can show."
   *
   * So unavailable manifests as EITHER:
   *   - `availablePaymentMethods === undefined`, OR
   *   - `availablePaymentMethods.amazonPay === false`
   *
   * Without this check we'd mark the form as ready, never call
   * `useExpressWalletFallback`, and strand the last-method
   * PREFERRED_PAYMENT_METHOD user on an invisible primary CTA.
   *
   * (`onLoadError` only fires for SDK-load failures, not for the
   * "device doesn't support Amazon Pay" case.)
   */
  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    if (event.availablePaymentMethods?.amazonPay !== true) {
      onUnavailable('device_unsupported');
      return;
    }
    onReady();
  };

  // `runPayment` (CheckoutContent) handles and surfaces its own errors
  // internally and never rethrows, so there is nothing for a try/catch here
  // to ever catch.
  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    await runPayment({ event, elements: elements!, stripe }, PaymentMethodEnum.AMAZON_PAY);
  };

  return (
    <AmazonPayButtonUI
      onReady={handleReady}
      onConfirm={handleConfirm}
      onLoadError={handleLoadError}
      onInvalidTaxClick={onInvalidTaxClick}
    />
  );
};

/**
 * Amazon Pay via Stripe's Express Checkout Element (one-click wallet
 * button). Mirrors {@link GooglePayStripeForm} / {@link ApplePayStripeForm}:
 * Stripe drives the Amazon auth popup itself and fires `onConfirm` with the
 * shopper's `billingDetails`. The confirm handler forwards to `runPayment`
 * tagged with `PaymentMethodEnum.AMAZON_PAY` — `CheckoutContent.runPayment`
 * treats that second arg as `payFromExpressCheckout` and skips the
 * hosted-redirect tab that the legacy PaymentElement Amazon Pay flow uses.
 *
 * This form is deliberately NOT wired into `PaymentMethodRenderer`'s
 * `SPECIAL_FORMS` map yet — the DEFAULT template still renders Amazon Pay
 * as a `<PaymentElement>` (see `StripeGenericPayForm`). Callers that want
 * the ECE button (e.g. PREFERRED's last-method branch) mount this
 * component directly. Both flows share `handleAmazonPayPayment`, which
 * dispatches on the presence of `event` in the payment params.
 *
 * Layout: wrapped in `WalletButtonShell` so the ECE iframe stays laid out
 * from t=0 (the shell layers placeholder + real via CSS Grid with
 * `opacity` transitions — never `display: none`). Amazon Pay's ECE probe
 * fires `onReady` with `availablePaymentMethods: undefined` when the
 * container has no measurable box at mount, and that path is
 * unrecoverable — see the block comment on `WalletButtonShell.wallet-btn`.
 * Dev testing note: Amazon Pay in the ECE probe only paints for buyers in
 * Amazon-supported regions (US/JP/EEA+UK). Localhost dev on a
 * non-supported IP will surface as `availablePaymentMethods: undefined`
 * even with the merchant fully activated — VPN to a supported region to
 * verify the button locally.
 */
export const AmazonPayStripeForm = ({ onInvalidTaxClick, isExpress }: AmazonPayStripeFormProps) => {
  const context = useContext(CheckoutContext);
  const { pricing } = context;
  const [state, setState] = useState<WalletReadyState>('loading');
  // Bridges Stripe ECE availability to the active template so PREFERRED
  // (last-method branch) can fall back to DEFAULT when Amazon Pay is
  // rejected at runtime (device unsupported / SDK load failure).
  const reportWalletAvailability = useExpressWalletFallback();
  const mountTimeRef = useRef(Date.now());

  const handleReady = () => {
    reportWalletAvailability(true, {
      wallet: 'amazon_pay',
      provider: ProviderEnum.STRIPE,
      loadTimeMs: Date.now() - mountTimeRef.current,
    });
    setState('ready');
  };
  const handleUnavailable = (reason: string) => {
    reportWalletAvailability(false, {
      wallet: 'amazon_pay',
      provider: ProviderEnum.STRIPE,
      reason,
      loadTimeMs: Date.now() - mountTimeRef.current,
    });
    setState('unavailable');
  };

  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: PaymentMethodEnum.AMAZON_PAY,
    isExpress: true,
  });

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress={isExpress} />;
  }

  return (
    <WalletButtonShell
      state={state}
      placeholder={<AmazonPayButtonPlaceholder />}
      testId="wallet-btn-stripe-amazon"
      usePlaceholderAsVisual
    >
      {stripe && !isStripeLoading && (
        <Elements
          stripe={stripe}
          options={{
            mode: 'payment',
            amount: Math.round(pricing.priceBreakdown.priceTotal),
            currency: pricing.currencyCode.toLowerCase(),
            loader: 'never',
            // Amazon Pay is its own PM type (`amazon_pay`) — not a card
            // wallet like Apple/Google Pay/Link — so it must be listed
            // explicitly. The Apple/Google/Link forms rely on
            // `EXPRESS_ELEMENTS_BASE_OPTIONS.paymentMethodTypes = ['card']`
            // because their wallets tokenize into `card`; that allow-list
            // filters `amazon_pay` out and the ECE probe returns
            // `availablePaymentMethods: undefined`.
            paymentMethodTypes: ['amazon_pay'],
            // NOTE: `paymentMethodCreation: 'manual'` is intentionally OMITTED.
            // Stripe's ECE probe drops redirect-based wallets (Amazon Pay is
            // one) from `availablePaymentMethods` when the Elements group is
            // in manual mode — the manual flow is designed for card-tokenizing
            // wallets (Apple/Google/Link) that hand back a PaymentMethod
            // client-side. Amazon Pay is confirmed via a BE-minted
            // PaymentIntent + `stripe.confirmPayment({ elements, clientSecret,
            // redirect: 'if_required' })` (see `handleStripeExpressCheckoutAmazonPay`
            // / `stripeService.confirmAmazonPayExpressPayment`), so we never
            // need the manual-mode client-side tokenization path.
          }}
        >
          <AmazonPayButton
            stripe={stripe}
            onReady={handleReady}
            onUnavailable={handleUnavailable}
            onInvalidTaxClick={onInvalidTaxClick}
          />
        </Elements>
      )}
    </WalletButtonShell>
  );
};
